import React from 'react';
import { EditableColumnType } from 'components/CustomTable/CustomTable';
import { DataType } from 'store/ducks/DataEntry/Branch/types';
import { DatePicker, Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import CustomSelect from 'components/CustomSelect';
import dayjs from 'dayjs';
import { IICTRiskType } from 'store/ducks/DataManagement/Article8AndICTSystem/types';
import * as Styled from '../Tables/DataEntry/DataEntry.styled';

export const getICTRiskColumns = (
  handleDateChange: (key: number, field: string, value: string) => void,
  handleSave: (record: IICTRiskType) => void,
  handleAddRow: (record: { key: React.Key }, index: number) => void,
  handleRemoveRow: (key: number) => void,
  handleInactivate: (record: IICTRiskType) => void,
  riskExposures: { value: string; label: string }[],
  riskLevels: { value: string; label: string }[],
  riskSources: { value: string; label: string }[],
  ictSystems: { value: string; label: string }[],
  t: any,
): EditableColumnType[] => [
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('ICT_RISK.RISK_NAME')}</p>
        </div>
      </Styled.TitleComponent>
    ),
    dataIndex: 'risk_name',
    editable: true,
    fixed: 'left',
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('ICT_RISK.ICT_SYSTEM')}</p>
        </div>
      </Styled.TitleComponent>
    ),
    dataIndex: 'ict_system_id',
    editable: false,
    render: (_, record) => (
      <CustomSelect
        options={ictSystems.map((item) => ({
          label: item.label,
          value: String(item.value),
        }))}
        value={record.ict_system_id ? String(record.ict_system_id) : undefined}
        onChange={(value: any, option: { label: string }) => {
          handleSave({
            ...record,
            ict_system_id: value,
            ict_system_name: option.label,
          } as IICTRiskType);
        }}
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('ICT_RISK.ICT_RISK_DESCRIPTION')}</p>
        </div>
      </Styled.TitleComponent>
    ),
    dataIndex: 'description',
    editable: true,
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('ICT_RISK.ICT_RISK_EXPOSURE')}</p>
        </div>
      </Styled.TitleComponent>
    ),
    dataIndex: 'risk_exposure_id',
    editable: false,
    render: (_, record) => (
      <CustomSelect
        options={riskExposures}
        value={record.risk_exposure_id}
        onChange={(value: any) =>
          handleSave({
            ...record,
            risk_exposure_id: value,
          } as IICTRiskType)
        }
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('ICT_RISK.RISK_LEVEL')}</p>
        </div>
      </Styled.TitleComponent>
    ),
    dataIndex: 'risk_level_id',
    editable: false,
    render: (_, record) => (
      <CustomSelect
        options={riskLevels}
        value={record.risk_level_id}
        onChange={(value: any) =>
          handleSave({
            ...record,
            risk_level_id: value,
          } as IICTRiskType)
        }
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('ICT_RISK.RISK_SOURCE')}</p>
        </div>
      </Styled.TitleComponent>
    ),
    dataIndex: 'risk_source_id',
    editable: false,
    render: (_, record) => (
      <CustomSelect
        options={riskSources}
        value={record.risk_source_id}
        onChange={(value: any) =>
          handleSave({
            ...record,
            risk_source_id: value,
          } as IICTRiskType)
        }
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  },
  {
    title: <>{t('ICT_RISK.ICT_RISK_VALIDITY')}</>,
    dataIndex: 'date_range',
    editable: false,
    render: (_: any, record: DataType) => {
      return (
        <Styled.DateInputs>
          <DatePicker
            value={record.start_date ? dayjs(record.start_date) : null}
            format="YYYY-MM-DD"
            onClick={(e) => e.stopPropagation()}
            onChange={(date) => {
              const formattedDateString = date
                ? dayjs(date).format('YYYY-MM-DD')
                : '1900-01-01';

              handleDateChange(record.key, 'start_date', formattedDateString);
            }}
          />
          <DatePicker
            value={
              record.end_date ? dayjs(record.end_date) : dayjs('9999-01-01')
            }
            format="YYYY-MM-DD"
            onClick={(e) => e.stopPropagation()}
            onChange={(date) => {
              const formattedDateString = date
                ? dayjs(date).format('YYYY-MM-DD')
                : '9999-01-01';

              handleDateChange(record.key, 'end_date', formattedDateString);
            }}
          />
        </Styled.DateInputs>
      );
    },
  },
  {
    title: t('GENERAL.ACTIONS'),
    key: 'actions',
    width: 150,
    render: (_: any, record) => (
      <>
        {record.isNew ? (
          <Button danger onClick={() => handleRemoveRow(record.key)}>
            {t('GENERAL.REMOVE')}
          </Button>
        ) : (
          <Button
            danger
            onClick={() => handleInactivate(record as IICTRiskType)}
          >
            {t('GENERAL.SOFT_DELETE')}
          </Button>
        )}
      </>
    ),
  },
];
